import { formatYearMonth } from '@hf/designSystem/v1';
import type { TearsheetReportStatus } from '@hf/sharedContract';

export type DashboardReportStatus = TearsheetReportStatus | 'missing';

export type DashboardReportRow = {
  id: string;
  fundId: string;
  fundName: string;
  reportDate: string;
  asOf: string;
  status: DashboardReportStatus;
  layout: string;
  layoutId?: string;
  owner: string;
  /** Aggregator viewing a referred-company fund — view/PDF only. */
  readOnly?: boolean;
  /** Set when the row is shown under a mixed fund / managed-account list. */
  itemKind?: 'fund' | 'managedAccount';
};

/** Inclusive lookback so From is 6 months before the current To month. */
export const DEFAULT_REPORT_MONTHS_BACK = 6;

const YEAR_MONTH_RE = /^\d{4}-\d{2}$/;

export function currentYearMonth(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function shiftYearMonth(value: string, delta: number): string {
  const [year, month] = value.split('-').map(Number) as [number, number];
  const index = year * 12 + (month - 1) + delta;
  const nextYear = Math.floor(index / 12);
  const nextMonth = (index % 12) + 1;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
}

export function defaultReportMonthRange(now = new Date()): { fromMonth: string; toMonth: string } {
  const toMonth = currentYearMonth(now);
  return { fromMonth: shiftYearMonth(toMonth, -DEFAULT_REPORT_MONTHS_BACK), toMonth };
}

function parseYearMonthParam(value: string | null | undefined): string | null {
  if (!value) return null;
  const month = value.slice(0, 7);
  return YEAR_MONTH_RE.test(month) ? month : null;
}

/**
 * Resolve reports From/To from URL params.
 * - to missing (or both missing): current month, from = to − 6 months
 * - from missing: from = to − 6 months
 * - to in the future is clamped to the current month
 */
export function resolveReportMonthRange(
  fromRaw: string | null | undefined,
  toRaw: string | null | undefined,
  now = new Date(),
): { fromMonth: string; toMonth: string } {
  const current = currentYearMonth(now);
  const parsedTo = parseYearMonthParam(toRaw);
  if (!parsedTo) return defaultReportMonthRange(now);
  const toMonth = compareYearMonth(parsedTo, current) > 0 ? current : parsedTo;
  const fromMonth = parseYearMonthParam(fromRaw);
  if (!fromMonth) return { fromMonth: shiftYearMonth(toMonth, -DEFAULT_REPORT_MONTHS_BACK), toMonth };
  return { fromMonth, toMonth };
}

export function compareYearMonth(a: string, b: string): number {
  return a.localeCompare(b);
}

export function yearMonthInRange(value: string, from: string, to: string): boolean {
  return compareYearMonth(value, from) >= 0 && compareYearMonth(value, to) <= 0;
}

export function yearMonthsInRange(from: string, to: string): string[] {
  const start = from <= to ? from : to;
  const end = from <= to ? to : from;
  const months: string[] = [];
  let [year, month] = start.split('-').map(Number) as [number, number];
  const [endYear, endMonth] = end.split('-').map(Number) as [number, number];
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

export function toTermYearMonth(value: string): string {
  return value.slice(0, 7);
}

/** Newest term month first; empty when the fund has no terms. */
export function latestTermYearMonth(termDates: string[]): string | null {
  let latest: string | null = null;
  for (const value of termDates) {
    const month = toTermYearMonth(value);
    if (!latest || compareYearMonth(month, latest) > 0) latest = month;
  }
  return latest;
}

export function latestAsOfMessage(latestYearMonth: string, entity = 'fund'): string {
  return `Latest as-of date for this ${entity} is ${formatYearMonth(latestYearMonth)}`;
}

function missingRow(fundId: string, fundName: string, reportDate: string, readOnly?: boolean): DashboardReportRow {
  return {
    id: `${fundId}:${reportDate}:missing`,
    fundId,
    fundName,
    reportDate,
    asOf: formatYearMonth(reportDate),
    status: 'missing',
    layout: '—',
    owner: '—',
    ...(readOnly ? { readOnly: true } : {}),
  };
}

/**
 * Real tearsheets in range plus one synthetic Missing row per fund × term month in range
 * that has no tearsheet. Months outside the fund’s terms are omitted (not “missing”).
 * Existing reports still appear even when that month is absent from the current terms set
 * (partner re-exports overwrite terms to the latest as-of).
 */
export function withMissingAsOf(
  funds: Array<{ fundId: string; fundName: string; readOnly?: boolean; termsKey?: string }>,
  reports: Array<{
    id: string;
    fundId: string;
    asOf: string;
    status: TearsheetReportStatus;
    layout: string;
    layoutId?: string;
    owner: string;
    readOnly?: boolean;
  }>,
  from: string,
  to: string,
  termsByFund: Map<string, Set<string>>,
): DashboardReportRow[] {
  const existing = new Set<string>();
  const rows: DashboardReportRow[] = [];
  const fundNameById = new Map(funds.map((fund) => [fund.fundId, fund.fundName]));
  const fundReadOnlyById = new Map(funds.map((fund) => [fund.fundId, fund.readOnly === true]));
  for (const report of reports) {
    const reportDate = report.asOf.slice(0, 7);
    if (!yearMonthInRange(reportDate, from, to)) continue;
    existing.add(`${report.fundId}:${reportDate}`);
    const readOnly = report.readOnly === true || fundReadOnlyById.get(report.fundId) === true;
    rows.push({
      id: report.id,
      fundId: report.fundId,
      fundName: fundNameById.get(report.fundId) ?? report.fundId,
      reportDate,
      asOf: formatYearMonth(reportDate),
      status: report.status,
      layout: report.layout,
      ...(report.layoutId ? { layoutId: report.layoutId } : {}),
      owner: report.owner,
      ...(readOnly ? { readOnly: true } : {}),
    });
  }
  for (const fund of funds) {
    const termMonths = termsByFund.get(fund.termsKey ?? fund.fundId);
    if (!termMonths) continue;
    for (const month of termMonths) {
      if (!yearMonthInRange(month, from, to)) continue;
      if (existing.has(`${fund.fundId}:${month}`)) continue;
      rows.push(missingRow(fund.fundId, fund.fundName, month, fund.readOnly === true));
    }
  }
  return rows.sort((a, b) => a.fundName.localeCompare(b.fundName) || compareYearMonth(b.reportDate, a.reportDate));
}
